<!--
     <CentroUserRoles
       :centros="centros"
       @action_new="action_new"
       @action_edit="action_edit"
       @action_delete="action_delete"
     />
-->

<template>
  <div class="ca-layout">
    <div class="ca-sub-header">
      <v-autocomplete
        style="flex-grow: 4"
        v-model="select_centro"
        :items="centro_list"
        dense filled outlined clearable color="primary"
        label="Centro a gestionar"
      />

      <v-autocomplete
        v-model="select_empresa"
        :items="empresa_list"
        dense filled outlined clearable color="primary"
        label="Empresa a gestionar"
      />

      <v-autocomplete
        v-model="select_department"
        :items="department_list"
        dense filled outlined clearable color="primary"
        label="Departamento"
      />

      <v-text-field
        v-model="filter_roles"
        dense filled outlined clearable color="primary"
        label="Buscar roles"
      />

      <v-text-field
        v-model="filter_user"
        dense filled outlined clearable color="primary"
        label="Buscar usuarios"
      />
    </div>

    <div
      class="ca-loading-cont"
      v-if="loading"
    >
      <div></div>
      <v-progress-circular
        indeterminate
        color="primary"
      ></v-progress-circular>
      <h2>Consultando datos...</h2>
    </div>

    <div v-else-if="show_welcome" class="ca-welcome">
      <v-icon size="160px" color="primary">mdi-arrow-up-left</v-icon>
      <h2>Selecciona un centro para gestionarlo</h2>
    </div>

    <div v-else class="ca-matrixcont">
      <div class="ca-explain">
        <div>
          Los roles van definidos por proceso. Los usuarios podran ver un centro solo si tienen uno o más roles. Es decir, si tenen al menos un [✔]
        </div>
      </div>

      <ManyToManyMatrix
        :key="rerender_m2mm"
        :cols="roles_list"
        :rows="user_list"
        @cellclick="manage_cell"
        @row_click="manage_user"
        @col_click="manage_role"
        column_name="Rol"
        row_name="Usuario"
        ref="m2mm"
      />
    </div>
    <ModalDialogForm
      :title="(selected_user || {}).text"
      :showModalDialog="user_modal"
      :fields_to_collect="fields_to_collect"
      clear_button_text="Cancelar"
      submit_button_text="Guardar"
      validate
      @close-dialog="hide_dialog"
      @clear="hide_dialog"
      @submit="submit_funct"
    />
  </div>
</template>


<style lang="scss">
 @import "@/sass/centroadmin.scss";
</style>


<script>
 import { mapState, mapActions, mapMutations, mapGetters } from 'vuex';
 import QCentro from '@/api/QCentro';
 import QProcess from '@/api/Process';
 import ManyToManyMatrix from '@/components/ManyToManyMatrix';
 import ModalDialogForm from '@/components/explorer/ModalDialog';

 function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
 }

 export default {
   name: 'CentroUserRoles',

   components: {
     ManyToManyMatrix,
     ModalDialogForm,
   },

   props: {
     centros: {required: true},
   },

   data: () => ({
     select_centro: null,
     select_empresa: null,
     select_department: null,
     filter_user: '',
     filter_roles: '',
     loading: false,
     centro_roles: null,
     rerender_m2mm: 0,
     user_modal: false,
     selected_user: null,
     fields_to_collect: null,
     submit_funct: () => {},
   }),

   computed: {
     ...mapGetters(['site_id']),
     ...mapState(['processes', 'users_map','sites']),

     show_welcome() {
       return !this.select_centro;
     },

     roles_list() {
       const search = (this.filter_roles || '').toLowerCase();

       if (!this.centro_roles || this.centro_roles.length <= 0)
         return [];

       const res = (Object
         .values(this.centro_roles || {})
         .filter(r => (!this.select_department || this.select_department == r.department)
                   && (!this.filter_roles || r.name.toLowerCase().indexOf(search) >= 0))
         .map(r => ({
           value: r.id,
           text: r.name,
           tooltip: r.name + (r.description ? "\n\n" + r.description : ""),
           rows: new Set(r.users),
         }))
         .sort((r1, r2) => (''+r1.text).localeCompare(''+r2.text))
       );
       return res;
     },

     user_list() {
       const search = (this.filter_user || '').toLowerCase();
       return (
         (Object.values(this.users_map) || [])
           .filter(us => !this.select_empresa || this.select_empresa == us.site)
           .map(us => ({
             value: us.id,
             text: `${us.last_name} ${us.first_name}`,
             tooltip: us.email,
           }))
           .filter(us => !search || us.text.toLowerCase().indexOf(search) >= 0)
           .sort((u1, u2) => (''+u1.text).localeCompare(u2.text))
       );
     },

     centro_list() {
       return (Object.values(this.centros) || [])
        .map(c => ({
          value: c.id,
          text: c.path_str,
        }))
        .sort((c1, c2) => (''+c1.text).localeCompare(''+c2.text));
     },

     department_list() {
       const done_ids = {};
       return (Object
         .values(this.centro_roles || {})
         .filter(x => !!(done_ids[x.department] = true)) // fancy trick
         .map(c => ({
           value: c.department,
           text: c.department_name,
         }))
         .sort((d1, d2) => (''+d1.text).localeCompare(''+d2.text))
       );
     },

     empresa_list() {
      if (!this.select_centro) {
        return [];
      }
      //Get site path
      let site_parent_path =this.sites.find(site => this.centros[this.select_centro].site == site.id).path.split('/')
      // Firts item is not a site
      site_parent_path.shift();
      // Firts: filter the ones we have. Second: take values needed for input
      return this.sites.filter(site => site_parent_path.includes(site.id+'')).map(site => ({'text': site.name,'value': site.id}));
     },
   },

   methods: {
     ...mapActions(['UPDATE_CENTRO_ACTION', 'GET_USERS']),

     async manage_cell(role_id, user_id) {
       const centro_id = this.select_centro;

       this.$refs.m2mm.set_loading(role_id, user_id, true);

       const check = this.centro_roles[role_id].users.indexOf(user_id) >= 0;
       const api_method = check ? QCentro.remove_user_role : QCentro.add_user_role;
       const users = (await api_method(this.site_id, centro_id, user_id, role_id)).users;

       await sleep(200); // TODO: Figth Vue reactivity to do this without the sleep
       await this.pull_centro_roles(centro_id);

       this.$refs.m2mm.set_loading(role_id, user_id, false);
     },

     async pull_centro_roles(centro) {
       // if no centro, this.select_centro will be used
       this.centro_roles = await QCentro.centro_roles(this.site_id, centro || this.select_centro);
     },

     fields_for_user(user) {
      return [
        [
          {
            id: 'user',
            label: "User",
            required: true,
            value: user.value,
            readonly: true,
            cols: 4,
            class: null,
            component: 'Select',
            placeholder: "",
            items: [[user.value,user.text]]
           },
          {
            id: 'roles',
            label: "User roles",
            required: false,
            value: this.roles_list.filter(it => it.rows.has(user.value)).map(it => it.value),
            readonly: false,
            cols: 8,
            class: null,
            component: 'AutocompleteSelect',
            placeholder: "",
            items: this.roles_list.map(it => [it.value,it.text]),
            multiple: true,
            chips: true,
           },
         ],
      ]
     },

     fields_for_rol(role) {
      return [
        [
          {
            id: 'role',
            label: "Rol",
            required: true,
            value: role.value,
            readonly: true,
            cols: 4,
            class: null,
            component: 'Select',
            placeholder: "",
            items: [[role.value,role.text]]
           },
          {
            id: 'users',
            label: "Users",
            required: false,
            value: Array.from(role.rows),
            readonly: false,
            cols: 8,
            class: null,
            component: 'AutocompleteSelect',
            placeholder: "",
            items: this.user_list.map(it => [it.value,it.text]),
            multiple: true,
            chips: true,
           },
         ],
         [
           {
             id: 'warning1',
             label: "This role will just be added to the users without it. It will not be removed from anyone.",
             required: false,
             value: "",
             readonly: true,
             hidden: null,
             cols: 6,
             class: 'warning',
             component: 'Tag',
             placeholder: '',
           }
         ]
      ]
     },

     manage_user(user) {
      this.fields_to_collect = this.fields_for_user(user);
      this.user_modal = true;
      this.selected_user = user;
      this.submit_funct = this.update_user
     },

     manage_role(role) {
      this.fields_to_collect = this.fields_for_rol(role);
      this.user_modal = true;
      this.submit_funct = this.update_role;
     },

     user_roles(user_id) {
      return (Object.values(this.centro_roles || {})).filter(it => it.users.includes(user_id)).map(it => it.id);
     },

     hide_dialog() {
      this.user_modal = false;
      this.selected_user = null;
     },

     async update_user(fd) {
      const roles = fd.get('roles')
      let set_roles = new Set();
      if (roles) {
        //This one is not longer used. The next code will override it. The result of this it's a Set of strings
        // e.j. Set ["[1", "2", "3]"]
        //TODO: remove
        set_roles = new Set((roles).split(','))
      }
      
      //Get all user roles
      let user_roles = this.user_roles(this.selected_user.value);
      //Parse an array string ("[x,y,z,...]") into an autentic array
      const set_roles_array = JSON.parse(roles || '[]');
      //Filter the removed roles.
      //Take the previous roles and the new ones and filter the available ones not selected on the result.
      const filtered_roles = ([...user_roles,...set_roles_array]).filter(
                                                                    it => !(this.roles_list.findIndex(rol => rol.value == it) >= 0 
                                                                    && !set_roles_array.includes(it))
                                                                  )
      //Return again as a Set of strings as before. e.j. -> ["[1", "2", "3]"]
      set_roles = new Set((JSON.stringify(filtered_roles)).split(','))

      const centro_id = this.select_centro;
      const users = (await QCentro.modify_user_roles(this.site_id, centro_id, this.selected_user.value, Array.from(set_roles))).users;

      await sleep(200); // TODO: Figth Vue reactivity to do this without the sleep
      await this.pull_centro_roles(centro_id);
      this.hide_dialog();
     },

     async update_role(form) {
      const role = form.get('role');
      const users = JSON.parse(form.get('users') || '[]');

      QCentro.modify_role_users(this.site_id, this.select_centro, role, users).then(()=>{
        this.pull_centro_roles(this.select_centro);
        this.hide_dialog();
      });
     },
   },

   async mounted() {
     if (Object.values(this.users_map).length <= 0) {
       await this.GET_USERS(this.site_id);
     }
   },

   watch: {
     async select_centro(new_centro) {
       if (new_centro) {
        this.select_empresa = (this.centros[this.select_centro] || {}).site;
         this.loading = true;
         await this.pull_centro_roles(new_centro);
         this.loading = false;
       }
     },
   }
 }
</script>
